import React, { useRef, useState } from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import {
  Menu,
  MenuAdd,
  MenuDelete,
  MenuEdit,
  MenuExport,
  MenuItem,
  MenuRecycle,
  MenuTableSettingToggle
} from '../../../../../shared/menu/menu'
import { useApiListGroup } from '../../../../../api-new/state/content'
import { CardFullHeightScrollY } from '../../../../../shared/card/card-full-height-scroll-y'
import { IconActivityLog, IconAdd, IconContent, IconGroup, IconUser } from '../../../../../shared/icon/icon'
import { useAuditCapabilities } from '../../../../../shared/audit/use-audit-capabilities'
import { useRoleOrgGroup, useRoleOrgGroupUser, useRoleOrgNodeGroup } from '../../../../../role/hook/role-org'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ColumnFormat, ColumnText, ColumnMenu } from '../../../../../shared/table-builder/table-columns'
import { MANAGED_GROUP_DELETE_NOTE, MANAGED_GROUP_LABEL, ManagedGroupChip, useManagedGroups } from '../../../../../shared/scim/managed-groups'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'

export const GroupList = () => {
  const apiListGroup = useApiListGroup()

  const roleOrgGroup = useRoleOrgGroup()
  const caps = useAuditCapabilities()
  const roleOrgNodeGroup = useRoleOrgNodeGroup()
  const roleOrgGroupUser = useRoleOrgGroupUser()
  // Groups the identity provider manages (SCIM Groups) carry its chip.
  const { managed } = useManagedGroups({ enabled: roleOrgGroup.accessRead })

  const handleReady = () => apiListGroup.get()
  const handleDestroyed = () => apiListGroup.init()

  // A group the identity provider manages is deleted only after the admin
  // reads what its next sync does (it makes the group again).
  const [deleting, setDeleting] = useState(null)
  const handleDelete = id => {
    if (managed(id) !== null) {
      setDeleting(id)
      return
    }
    apiListGroup.delete(id)
  }
  const confirmDelete = () => {
    const id = deleting
    setDeleting(null)
    apiListGroup.delete(id)
  }

  const tableRef = useRef()
  const handleExportCsv = () => tableRef.current.exportCsv('groups')
  const handleTableSettingToggle = () => tableRef.current.toggleSideBar()

  const HeaderMenu = () =>
    <Menu>
      <MenuAdd text="add group" to="/org/group/list/add" show={roleOrgGroup.accessCreate} />
      <MenuExport onClick={handleExportCsv} show={roleOrgGroup.accessRead} />
      <MenuItem Icon={IconActivityLog} text="audit log" to="/org/audit-log?type=groups" show={caps.canAudit} />
      <MenuRecycle to="/org/group/list/recycle" show={roleOrgGroup.accessDelete} />
      <MenuTableSettingToggle onClick={handleTableSettingToggle} show />
    </Menu>

  return (
    <React.Fragment>
      <ConfirmDialog
        open={deleting !== null}
        title="Delete a group your identity provider manages?"
        body={MANAGED_GROUP_DELETE_NOTE}
        confirmText="Delete group"
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)} />
      <BreadcrumbView Icon={IconGroup} text="Groups">
        <CardFullHeightScrollY title="groups" HeaderMenu={HeaderMenu}>
          <TableAgClient
            ref={tableRef}
            apiList={apiListGroup}
            onReady={handleReady}
            onDestoryed={handleDestroyed}
            emptyState={{
              Icon: IconGroup,
              title: 'No groups yet',
              message: 'Create a group to give several people access to the same content.',
              action: { label: 'Add group', Icon: IconAdd, to: '/org/group/list/add', show: roleOrgGroup.accessCreate }
            }}>
            <ColumnText field="name" />
            <ColumnFormat
              field="id"
              header="Provisioning"
              render={cell => (cell.data && managed(cell.data.id) !== null ? <ManagedGroupChip connectionName={managed(cell.data.id)} /> : '')}
              exportValue={row => (managed(row.id) !== null ? MANAGED_GROUP_LABEL : '')}
              fit="content"
            />
            <ColumnMenu
              menu={row =>
                <Menu small>
                  <MenuEdit to={`/org/group/list/edit/${row.id}`} show={roleOrgGroup.accessRead} />
                  <MenuItem Icon={IconContent} text="contents" to={`/org/group/list/edit/${row.id}/content`} show={roleOrgNodeGroup.accessRead} />
                  <MenuItem Icon={IconUser} text="users" to={`/org/group/list/edit/${row.id}/user`} show={roleOrgGroupUser.accessRead} />
                  <MenuDelete onClick={() => handleDelete(row.id)} show={roleOrgGroup.accessDelete} />
                </Menu>}
            />
          </TableAgClient>
        </CardFullHeightScrollY>
      </BreadcrumbView>
    </React.Fragment>
  )
}
